import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import { ADMIN_DOMAINS, buildAdminTarget } from '../adminNavConfig';
import { PARTNER_ADMIN_DOMAINS } from '../partnerAdminNavConfig';

function isEditableTarget(target) {
  if (!target || !(target instanceof HTMLElement)) {
    return false;
  }

  const tagName = target.tagName;
  return target.isContentEditable || tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT';
}

function findVisibleSearchInput() {
  const openDialog = document.querySelector('.au-spotlight__panel [data-admin-search-input="true"]');
  if (openDialog instanceof HTMLInputElement && !openDialog.disabled) {
    return openDialog;
  }

  const inputs = Array.from(document.querySelectorAll('.admin-layout [data-admin-search-input="true"]'));
  return inputs.find((node) => (
    node instanceof HTMLInputElement
    && !node.disabled
    && node.offsetParent !== null
  )) || null;
}

export function focusNearestAdminSearchInput() {
  if (typeof document === 'undefined') {
    return false;
  }

  const input = findVisibleSearchInput();
  if (!input) {
    return false;
  }

  input.focus();
  input.select();
  return true;
}

/**
 * @param {{
 *   onOpenSpotlight?: (() => void) | null,
 *   onOpenShortcuts?: (() => void) | null,
 *   onCloseShortcuts?: (() => void) | null
 * }} options
 */
export default function useAdminShortcuts({
  onOpenSpotlight = null,
  onOpenShortcuts = null,
  onCloseShortcuts = null,
} = {}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const leaderRef = useRef(null);
  const timeoutRef = useRef(null);
  const isPartnerAdmin = user?.role === 'partner_admin';
  const domains = isPartnerAdmin ? PARTNER_ADMIN_DOMAINS : ADMIN_DOMAINS;
  const basePath = isPartnerAdmin ? '/partner-admin' : '/admin';

  useEffect(() => {
    const clearLeader = () => {
      leaderRef.current = null;
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };

    const beginLeader = () => {
      clearLeader();
      leaderRef.current = 'g';
      timeoutRef.current = window.setTimeout(clearLeader, 900);
    };

    const handleKeyDown = (event) => {
      const editable = isEditableTarget(event.target);

      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        onOpenSpotlight?.();
        return;
      }

      if (event.key === 'Escape') {
        clearLeader();
        onCloseShortcuts?.();
        return;
      }

      if (editable || event.altKey || event.metaKey || event.ctrlKey) {
        return;
      }

      if (leaderRef.current === 'g' && /^[1-5]$/.test(event.key)) {
        const index = Number(event.key) - 1;
        const domain = domains[index];
        clearLeader();
        if (domain) {
          event.preventDefault();
          navigate(buildAdminTarget(domain.key, domain.defaultTool, null, basePath));
        }
        return;
      }

      if (event.key.toLowerCase() === 'g') {
        beginLeader();
        return;
      }

      clearLeader();

      if (event.key === '/') {
        const focused = focusNearestAdminSearchInput();
        if (focused) {
          event.preventDefault();
        }
        return;
      }

      if (event.key === '?') {
        event.preventDefault();
        onOpenShortcuts?.();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      clearLeader();
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [basePath, domains, navigate, onCloseShortcuts, onOpenShortcuts, onOpenSpotlight]);
}
